import { Form, Input } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";

// FIO T2 benefName: max 35 characters, letters (diacritics are transliterated on send), digits, space and , . / -
const MAX_LENGTH = 35;
const PATTERN = /^[\p{L}\p{N} ,./-]+$/u;

// Characters the API's ICU Latin-ASCII transliteration turns into several characters
const EXPANSIONS = {
  ß: "ss", æ: "ae", œ: "oe", þ: "th", Æ: "AE", Œ: "OE", Þ: "TH",
  "€": "EUR", "…": "...", "©": "(C)", "®": "(R)", "™": "TM", "½": " 1/2", "¼": " 1/4", "¾": " 3/4",
};
const EXPANSION_PATTERN = new RegExp(`[${Object.keys(EXPANSIONS).join("")}]`, "g");

// Length as the API counts it after transliterating to ASCII: diacritics are dropped, the characters above expand
export const fioTextLength = (value = "") =>
  value
    .replace(EXPANSION_PATTERN, (char) => EXPANSIONS[char])
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim().length;

/**
 * Account Bank Owner Input Field
 * @component
 * @alias AccountBankOwnerInput
 * @property {string} initialValue - init value for account holder Form Item
 * @property {bool} readOnly - sets Form Item read-only
 * @property {bool} required - sets Form Items required - default false
 * @property {bool} strict - applies the FIO rules for EUR payments - default true
 * @property {node} extra - replaces the default help text
 * @returns <Form.Item />
 */
const AccountBankOwnerInput = ({ initialValue, readOnly, required = false, strict = true, extra }) => {
  const { t } = useTranslation();

  const rules = [
    {
      required: required,
      message: t("finance.accounts.validation.accountBankOwner"),
    },
  ];

  if (strict) {
    rules.push({
      validator: (_, value) =>
        // modifier letters like ʼ are letters here but become ' in ASCII, which FIO rejects
        !value || (PATTERN.test(value) && !/\p{Lm}/u.test(value) && fioTextLength(value) <= MAX_LENGTH)
          ? Promise.resolve()
          : Promise.reject(new Error(t("finance.accounts.validation.accountBankOwnerFormat"))),
    });
  }

  return (
    <Form.Item
      label={t("finance.accounts.accountBankOwner")}
      name={"account_bank_owner"}
      initialValue={initialValue}
      extra={extra ?? (!readOnly && strict && t("finance.accounts.accountBankOwnerHelp"))}
      rules={rules}
    >
      <Input
        maxLength={strict ? MAX_LENGTH : undefined}
        showCount={!readOnly && strict}
        readOnly={readOnly}
        bordered={!readOnly}
      ></Input>
    </Form.Item>
  );
};

export default AccountBankOwnerInput;
